import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ApiSettingsTab = () => {
  const [apiKeys, setApiKeys] = useState({
    gemini: "gm_••••••••••••••••••••••••••••••••••••••••",
    mistral: "ms_••••••••••••••••••••••••••••••••••••••••"
  });

  const [testResults, setTestResults] = useState({
    gemini: null,
    mistral: null
  });

  const [isTestingConnection, setIsTestingConnection] = useState({
    gemini: false,
    mistral: false
  });

  const usageData = [
    {
      service: "Gemini 2.0 Flash",
      requests: 2847,
      tokens: 1250000,
      cost: "£47.85",
      lastUsed: "2025-10-14 11:45:23",
      status: "active"
    },
    {
      service: "Mistral Large",
      requests: 156,
      tokens: 89000,
      cost: "£12.30",
      lastUsed: "2025-10-14 09:22:15",
      status: "active"
    }
  ];

  const handleApiKeyChange = (service, value) => {
    setApiKeys(prev => ({
      ...prev,
      [service]: value
    }));
  };

  const handleTestConnection = async (service) => {
    setIsTestingConnection(prev => ({ ...prev, [service]: true }));
    
    // Simulate API test
    setTimeout(() => {
      const isSuccess = Math.random() > 0.2; // 80% success rate
      setTestResults(prev => ({
        ...prev,
        [service]: {
          success: isSuccess,
          message: isSuccess 
            ? "Connection successful" :"Invalid API key or service unavailable",
          timestamp: new Date()?.toLocaleString('en-GB')
        }
      }));
      setIsTestingConnection(prev => ({ ...prev, [service]: false }));
    }, 2000);
  };

  const handleSaveApiKeys = () => {
    // Mock save functionality
    console.log('Saving API keys:', apiKeys);
  };

  return (
    <div className="space-y-8">
      {/* API Key Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Key" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">API Key Management</h3>
            <p className="text-sm text-muted-foreground">Configure your OCR service API keys</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gemini API */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="Zap" size={16} className="text-primary" />
              <h4 className="font-medium text-foreground">Gemini 2.0 Flash</h4>
              <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">Primary</span>
            </div>
            
            <Input
              type="password"
              label="API Key"
              value={apiKeys?.gemini}
              onChange={(e) => handleApiKeyChange('gemini', e?.target?.value)}
              placeholder="Enter your Gemini API key"
              description="Used for primary OCR processing"
            />

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestConnection('gemini')}
                loading={isTestingConnection?.gemini}
                iconName="TestTube"
                iconPosition="left"
              >
                Test Connection
              </Button>

              {testResults?.gemini && (
                <div className={`flex items-center space-x-2 text-sm ${
                  testResults?.gemini?.success ? 'text-success' : 'text-error'
                }`}>
                  <Icon 
                    name={testResults?.gemini?.success ? "CheckCircle" : "XCircle"} 
                    size={16} 
                  />
                  <span>{testResults?.gemini?.message}</span>
                </div>
              )}
            </div>
          </div>

          {/* Mistral API */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="RefreshCw" size={16} className="text-secondary" />
              <h4 className="font-medium text-foreground">Mistral Large</h4>
              <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full">Fallback</span>
            </div>
            
            <Input
              type="password"
              label="API Key"
              value={apiKeys?.mistral}
              onChange={(e) => handleApiKeyChange('mistral', e?.target?.value)}
              placeholder="Enter your Mistral API key"
              description="Used for deep reprocessing"
            />

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestConnection('mistral')}
                loading={isTestingConnection?.mistral}
                iconName="TestTube"
                iconPosition="left"
              >
                Test Connection
              </Button>

              {testResults?.mistral && (
                <div className={`flex items-center space-x-2 text-sm ${
                  testResults?.mistral?.success ? 'text-success' : 'text-error'
                }`}>
                  <Icon 
                    name={testResults?.mistral?.success ? "CheckCircle" : "XCircle"} 
                    size={16} 
                  />
                  <span>{testResults?.mistral?.message}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-6 border-t border-border">
          <Button onClick={handleSaveApiKeys} iconName="Save" iconPosition="left">
            Save API Keys
          </Button>
        </div>
      </div>
      {/* Usage Monitoring */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Usage Monitoring</h3>
              <p className="text-sm text-muted-foreground">Track API usage and costs</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Usage
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-foreground">Service</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Requests</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Tokens</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Cost</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Last Used</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {usageData?.map((usage, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={usage?.service?.includes('Gemini') ? 'Zap' : 'RefreshCw'} 
                        size={16} 
                        className={usage?.service?.includes('Gemini') ? 'text-primary' : 'text-secondary'}
                      />
                      <span className="font-medium text-foreground">{usage?.service}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-foreground">{usage?.requests?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-foreground">{usage?.tokens?.toLocaleString()}</td>
                  <td className="py-3 px-4 font-medium text-foreground">{usage?.cost}</td>
                  <td className="py-3 px-4 text-muted-foreground text-sm">{usage?.lastUsed}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full capitalize">
                      {usage?.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">3,003</div>
              <div className="text-sm text-muted-foreground">Total Requests</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent">1.34M</div>
              <div className="text-sm text-muted-foreground">Total Tokens</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">£60.15</div>
              <div className="text-sm text-muted-foreground">Monthly Cost</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiSettingsTab;